import type { ChatProject } from '@/api';
import type { OutputNode } from './outputFiles';
import { overlayLiveRun, type RunScoreRecord, type ScoreCheck } from './runScorePanel';

export type AnalyzePlaybook = {
  skill_id: string;
  bullets: { content: string; helpful: number; harmful: number }[];
};

export type LiveAnalyzeOverlay = {
  runId: string | null;
  running: boolean;
  elapsedMs: number;
  userScore: number | null;
  llmScore: number | null;
  reviewMd: string;
  checks: ScoreCheck[];
  model?: string;
  promptTokens?: number | null;
};

export function playbooksFromArtifacts(tree: OutputNode[]): AnalyzePlaybook[] {
  const out: AnalyzePlaybook[] = [];
  const walk = (nodes: OutputNode[] | undefined, trail: string[]) => {
    for (const n of nodes || []) {
      const path = trail.concat(n.name || '');
      if (n.children) walk(n.children, path);
      if (n.kind !== 'file' || !n.name.endsWith('.json')) continue;
      if (!path.some((p) => p === 'playbook')) continue;
      const bullets = parseBullets(n.content);
      const skillId = n.name.replace(/\.json$/i, '').trim() || 'skill';
      out.push({ skill_id: skillId, bullets });
    }
  };
  walk(tree, []);
  return out;
}

function parseBullets(raw: string | undefined): AnalyzePlaybook['bullets'] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((b): b is Record<string, unknown> => !!b && typeof b === 'object')
      .map((b) => ({
        content: typeof b.content === 'string' ? b.content : '',
        helpful: typeof b.helpful === 'number' ? b.helpful : 0,
        harmful: typeof b.harmful === 'number' ? b.harmful : 0,
      }))
      .filter((b) => b.content.trim());
  } catch {
    return [];
  }
}

export function packAnalyzeRuns(history: RunScoreRecord[], live?: LiveAnalyzeOverlay): ChatProject['runs'] {
  const rows = live ? overlayLiveRun(history, live) : history;
  return rows.map((r) => ({
    run_id: r.runId,
    model: r.model || '',
    prompt_tokens: r.promptTokens,
    duration_ms: r.durationMs,
    user_score: r.userScore,
    llm_score: r.llmScore,
    review_md: r.reviewMd || '',
    checks: (r.checks || []).map((c) => ({ id: c.id, passed: c.passed, reason: c.reason || '' })),
  }));
}
